/**
 * Typed client for the creator/affiliate program (/api/affiliates/*).
 */
import { api } from "@/lib/api";

export interface Affiliate {
  id: string;
  status: "pending" | "approved" | "rejected" | "suspended";
  first_name: string;
  last_name: string;
  email: string;
  social_links: string;
  previous_customer: boolean | null;
  follows_social: boolean | null;
  dance_context: string | null;
  notes: string | null;
  discount_code: string | null;
  type: "creator" | "ambassador";
  commission_rate: string;
  code_status: "none" | "active" | "suspended";
  created_at: string;
  approved_at: string | null;
  sales_count: string;
  earned: string | null;
  paid: string | null;
}

export interface AffiliateSale {
  id: string;
  order_name: string;
  order_date: string;
  order_total: string;
  commission_base: string;
  commission: string;
}

export interface AffiliatePayout {
  id: string;
  amount: string;
  method: string | null;
  reference: string | null;
  note: string | null;
  paid_at: string;
}

export interface AffiliateDetail {
  affiliate: Affiliate;
  sales: AffiliateSale[];
  payouts: AffiliatePayout[];
  brand_logo_url: string | null;
}

export const affiliatesApi = {
  list: () => api.get<{ affiliates: Affiliate[] }>("/affiliates"),
  get: (id: string) => api.get<AffiliateDetail>(`/affiliates/${id}`),
  approve: (
    id: string,
    opts?: {
      commission_rate?: number;
      code?: string;
      customer_discount_pct?: number;
      welcome_email_subject?: string;
      welcome_email_message?: string;
    },
  ) => api.post<{ affiliate: Affiliate }>(`/affiliates/${id}/approve`, opts ?? {}),
  addDirect: (
    body: {
      type: "creator" | "ambassador";
      first_name: string;
      last_name?: string;
      email: string;
      social_links?: string;
      commission_rate?: number;
      customer_discount_pct?: number;
      code?: string;
      notes?: string;
    },
  ) => api.post<{ affiliate: Affiliate }>("/affiliates/direct", body),
  reject: (id: string) => api.post<{ affiliate: Affiliate }>(`/affiliates/${id}/reject`),
  suspend: (id: string) => api.post<{ affiliate: Affiliate }>(`/affiliates/${id}/suspend`),
  activate: (id: string) => api.post<{ affiliate: Affiliate }>(`/affiliates/${id}/activate`),
  payout: (id: string, body: { amount: number; method?: string; reference?: string; note?: string }) =>
    api.post<{ ok: true; earned: number; paid: number; owed: number }>(`/affiliates/${id}/payout`, body),
  sync: () => api.post<{ ok: true; summary: { scanned: number; attributed: number; inserted: number; skippedUnmatched: number } }>("/affiliates/sync"),
};
